/**
 * Línea de tiempo del paciente: los tratamientos realizados (con lo pagado y el saldo de cada uno), los abonos y lo que se cargó con el
 * «historial anterior». Se ordena del más actual al más viejo (o al revés) y se filtra por pagados o con saldo pendiente.
 */
import { useEffect, useState } from "react";
import { api, puede, type Paciente } from "../api.ts";
import { dinero } from "../../../dominio/src/index.ts";
import { AvisoError, Seccion, useAccion } from "./comun.tsx";

interface Evento {
  clave: string; id: string; tipo: "tratamiento" | "abono" | "previo"; fecha: string; titulo: string; diente: number | null; superficies: string; profesional: string;
  valor: number; pagado: number; saldo: number; anterior: boolean; cerrado: boolean; nota: string; recibo?: number; metodo?: string;
}
type Filtro = "todo" | "pagados" | "saldo";

const fechaLarga = (iso: string) => (iso ? new Date(iso + "T12:00:00").toLocaleDateString("es-EC", { day: "numeric", month: "short", year: "numeric" }) : "—");

export function LineaTiempo({ paciente, irA }: { paciente: Paciente; irA: (t: string) => void }) {
  const [datos, setDatos] = useState<{ eventos: Evento[]; saldoTotal: number } | null>(null);
  const [orden, setOrden] = useState<"nuevo" | "viejo">("nuevo");
  const [filtro, setFiltro] = useState<Filtro>("todo");
  const a = useAccion();
  const cargar = () => api<{ eventos: Evento[]; saldoTotal: number }>("GET", `/api/pacientes/${paciente.id}/linea-tiempo`).then(setDatos).catch(a.setError);
  useEffect(() => { cargar(); }, [paciente.id]);
  if (!datos) return a.error ? <AvisoError error={a.error} /> : <p className="muted">Cargando…</p>;

  const lista = datos.eventos
    .filter((e) => filtro === "todo" ? true : e.tipo === "tratamiento" && (filtro === "saldo" ? e.saldo > 0 : e.saldo === 0))
    .sort((x, y) => (orden === "nuevo" ? y.fecha.localeCompare(x.fecha) : x.fecha.localeCompare(y.fecha)) || x.clave.localeCompare(y.clave));
  const conSaldo = datos.eventos.filter((e) => e.tipo === "tratamiento" && e.saldo > 0);
  const anios = [...new Set(lista.map((e) => e.fecha.slice(0, 4)))];

  return (
    <Seccion titulo="Línea de tiempo">
      <div className="fila" style={{ alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div className="seg" role="group" aria-label="Orden">
          <button aria-pressed={orden === "nuevo"} onClick={() => setOrden("nuevo")}>Más actual primero</button>
          <button aria-pressed={orden === "viejo"} onClick={() => setOrden("viejo")}>Más antiguo primero</button>
        </div>
        <div className="seg" role="group" aria-label="Filtro">
          <button aria-pressed={filtro === "todo"} onClick={() => setFiltro("todo")}>Todo</button>
          <button aria-pressed={filtro === "pagados"} onClick={() => setFiltro("pagados")}>Tratamientos pagados</button>
          <button aria-pressed={filtro === "saldo"} onClick={() => setFiltro("saldo")}>Con saldo pendiente{conSaldo.length > 0 && ` (${conSaldo.length})`}</button>
        </div>
      </div>
      {conSaldo.length > 0 && (
        <div className="aviso error" role="status">
          <b>Saldo pendiente: {dinero(conSaldo.reduce((s, e) => s + e.saldo, 0))}</b> en {conSaldo.length} {conSaldo.length === 1 ? "tratamiento" : "tratamientos"}.
          {puede("caja:leer") && <> <button className="enlace" onClick={() => irA("plan")}>Ver en Plan y pagos</button></>}
        </div>
      )}
      <AvisoError error={a.error} />
      {lista.length === 0 && <p className="muted" style={{ margin: 0 }}>{filtro === "todo" ? "Todavía no hay tratamientos realizados ni abonos." : filtro === "saldo" ? "No hay tratamientos con saldo pendiente." : "No hay tratamientos pagados."}</p>}
      {anios.map((anio) => (
        <div key={anio} className="linea-tiempo">
          <h3 style={{ fontSize: 14, color: "var(--muted)", margin: "8px 0 4px" }}>{anio}</h3>
          <ul className="lista-tiempo">
            {lista.filter((e) => e.fecha.startsWith(anio)).map((e) => (
              <li key={e.clave} className={`${e.tipo}${e.saldo > 0 ? " con-saldo" : ""}`}>
                <span className="mono fecha">{fechaLarga(e.fecha)}</span>
                <span className="detalle">
                  <b>{e.titulo}</b>{e.diente !== null && <span className="mono muted"> · pieza {e.diente}{e.superficies ? ` ${e.superficies}` : ""}</span>}
                  {e.tipo === "abono" && e.metodo && <span className="muted"> · {e.metodo}</span>}
                  {e.profesional && <span className="muted"> · {e.tipo === "abono" ? `registró ${e.profesional}` : `Dr. ${e.profesional.replace(/^Dr\.?a?\s+/i, "")}`}</span>}
                  {e.tipo === "previo" && <span className="chip" style={{ marginLeft: 6 }} title="Registrado con el historial anterior: sin valores">Historial anterior</span>}
                  {e.tipo === "tratamiento" && e.anterior && <span className="chip" style={{ marginLeft: 6 }} title="Tratamiento anterior registrado en Plan y pagos">Anterior</span>}
                  {e.nota && <span className="muted"> · {e.nota}</span>}
                </span>
                <span className="dinero">
                  {e.tipo === "tratamiento" && <>
                    <span className="mono">{dinero(e.valor)}</span>
                    {e.saldo > 0
                      ? <span className="chip rojo" title={`Pagado ${dinero(e.pagado)} de ${dinero(e.valor)}`}>⚠ Saldo {dinero(e.saldo)}</span>
                      : <span className="chip azul">Pagado</span>}
                  </>}
                  {e.tipo === "abono" && <b className="mono" style={{ color: "var(--blue)" }}>+ {dinero(e.valor)}</b>}
                  {e.tipo === "previo" && puede("clinico:escribir") && <button className="btn chico peligro" aria-label={`Quitar ${e.titulo}`} onClick={() => { if (window.confirm("¿Quitar este registro del historial anterior? También sale del odontograma.")) a.correr(() => api("DELETE", `/api/tratamientos-previos/${e.id}`)).then((r) => { if (r) cargar(); }); }}>Quitar</button>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>Para agregar un tratamiento de antes (con su fecha, quién lo hizo, su valor y lo que ya pagó) ve a <button className="enlace" onClick={() => irA("plan")}>Plan y pagos</button>.</p>
    </Seccion>
  );
}
